import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  Shield, Download, Trash2, FileText, AlertTriangle,
  CheckCircle, Clock, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TenantGDPRSettings() {
  const { t } = useI18n();
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletionRequest, setDeletionRequest] = useState(null);

  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Request export to get an ID
      const reqRes = await axios.post(`${API}/gdpr/export/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const exportId = reqRes.data.export_id;
      
      // Download the export
      const response = await axios.get(`${API}/gdpr/export/${exportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Trigger file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `recruitizr_data_export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleDeleteRequest = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/gdpr/deletion/request`, {
        reason: deleteReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDeletionRequest(response.data);
      setDeleteDialogOpen(false);
      toast.success('Deletion request submitted');
    } catch (error) {
      toast.error('Failed to submit deletion request');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-violet-600" />
          Privacy & Data Settings
        </h1>
        <p className="text-slate-500 mt-1">Manage your data and privacy preferences — aligned with DPDP Act 2023 and GDPR principles</p>
      </div>

      {/* Deletion Request Status */}
      {deletionRequest && (
        <Alert className="bg-amber-50 border-amber-200">
          <Clock className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-700">
            <strong>Deletion Request Submitted</strong> - Request ID: {deletionRequest.request_id}. 
            Expected completion: within 30 days, in line with applicable data-protection regulations.
          </AlertDescription>
        </Alert>
      )}

      {/* Your Rights */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Your Data Rights</CardTitle>
          <CardDescription>You have the following rights over your personal data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'Right to Access', desc: 'View all data we hold about you' },
              { title: 'Right to Rectification', desc: 'Correct inaccurate personal data' },
              { title: 'Right to Erasure', desc: 'Request deletion of your data' },
              { title: 'Right to Portability', desc: 'Export your data in a standard format' },
              { title: 'Right to Object', desc: 'Object to processing of your data' },
              { title: 'Right to Withdraw Consent', desc: 'Withdraw consent at any time' }
            ].map((right, idx) => (
              <div key={`tena-${idx}`} className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  <h4 className="font-medium text-slate-900">{right.title}</h4>
                </div>
                <p className="text-sm text-slate-600">{right.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Data Export */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-blue-600" />
            Export Your Data
          </CardTitle>
          <CardDescription>
            Download a complete copy of all data associated with your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <h4 className="font-medium text-blue-900">Full Data Export</h4>
              <p className="text-sm text-blue-700 mt-1">
                Includes users, positions, resumes, interviews, and all organization data in JSON format
              </p>
            </div>
            <Button
              onClick={handleExportData}
              disabled={exportLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="gdpr-export-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {exportLoading ? 'Preparing...' : 'Download Export'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Deletion */}
      <Card className="border-slate-200 border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" />
            Delete Your Data
          </CardTitle>
          <CardDescription>
            Request permanent deletion of your organization and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="bg-red-50 border-red-200 mb-4">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-700">
              <strong>Warning:</strong> This action is irreversible. All users, positions, resumes, 
              interviews, and other data will be permanently deleted. This process may take up to 30 days.
            </AlertDescription>
          </Alert>
          
          <div className="p-4 border border-red-200 rounded-lg">
            <h4 className="font-medium text-red-900 mb-2">Before you proceed:</h4>
            <ul className="text-sm text-red-700 space-y-1 mb-4">
              <li>• Export your data first if you need a backup</li>
              <li>• Cancel any active subscriptions</li>
              <li>• Inform your team members about the deletion</li>
              <li>• This cannot be undone</li>
            </ul>
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="gdpr-delete-btn"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Request Data Deletion
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Privacy Resources */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-violet-600" />
            Privacy Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a 
              href="/privacy-policy" 
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">Privacy Policy</span>
                <ExternalLink className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 mt-1">How we collect and use your data</p>
            </a>
            <a 
              href="/cookie-policy" 
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">Cookie Policy</span>
                <ExternalLink className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 mt-1">Information about cookies we use</p>
            </a>
            <a 
              href="mailto:privacy@recruitizr.com" 
              className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-900">Contact DPO</span>
                <ExternalLink className="h-4 w-4 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500 mt-1">Reach our Data Protection Officer</p>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Confirm Data Deletion Request
            </DialogTitle>
            <DialogDescription>
              This will submit a request to permanently delete all your organization's data. 
              The process will be completed within 30 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Reason for deletion (optional)
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Please let us know why you're leaving..."
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteRequest}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteLoading ? 'Submitting...' : 'Confirm Deletion Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
